import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  createProject,
  runProject,
  uploadProjectFile,
  waitForProjectReady,
  type FileRole,
  type ImagePolicy,
  type ProjectMode,
  type SourceTier,
} from "@/api/projects";
import { generationErrorMessage } from "@/api/llm-access";
import { Button, Input, Alert, Spinner } from "@/components/ui";

interface PickedFile {
  file: File;
  relativePath: string;
  role: FileRole;
  imagePolicy: ImagePolicy;
  tier: SourceTier;
}

const SUPPORTED = [".pdf", ".docx", ".txt", ".md", ".jpg", ".jpeg", ".png", ".heic", ".heif", ".webp"];
const TASK_HINTS = ["assignment", "task", "exercise", "problem", "bonus", "homework", "aufgabe", "ubung", "übung"];

function isSupported(name: string): boolean {
  const lower = name.toLowerCase();
  return SUPPORTED.some((ext) => lower.endsWith(ext));
}
function isJunk(path: string): boolean {
  return path.split("/").some((seg) => seg.startsWith("."));
}
function looksLikeTask(name: string): boolean {
  const lower = name.toLowerCase();
  return TASK_HINTS.some((h) => lower.includes(h));
}

const LANGUAGES: { code: string; label: string }[] = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
  { code: "ru", label: "Русский" },
  { code: "tr", label: "Türkçe" },
  { code: "ar", label: "العربية" },
  { code: "hi", label: "हिन्दी" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
];

export function ProjectCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("en");
  const [mode, setModeRaw] = useState<ProjectMode>("single");
  const [brief, setBrief] = useState("");
  const [withSynthesis, setWithSynthesis] = useState(false);
  const [picked, setPicked] = useState<PickedFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const filesInput = useRef<HTMLInputElement>(null);

  const taskCount = useMemo(() => picked.filter((p) => p.role === "task").length, [picked]);

  // Switching to material demotes any task roles (there is no task in that
  // mode); switching away re-runs the auto-suggest on the next addFiles.
  const setMode = (m: ProjectMode) => {
    setModeRaw(m);
    if (m === "material") {
      setPicked((prev) => prev.map((p) => (p.role === "task" ? { ...p, role: "info" } : p)));
    }
  };

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const next: PickedFile[] = [];
    for (const file of Array.from(fileList)) {
      const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
      if (isJunk(rel) || file.size === 0 || !isSupported(file.name)) continue;
      next.push({ file, relativePath: rel, role: "info", imagePolicy: "all", tier: "main" });
    }
    // Auto-suggest exactly one task if none yet (not in material mode — no task there).
    setPicked((prev) => {
      const merged = [...prev, ...next];
      if (mode !== "material" && !merged.some((p) => p.role === "task")) {
        const guess = merged.find((p) => looksLikeTask(p.file.name));
        if (guess) guess.role = "task";
      }
      // de-dup by relativePath
      const seen = new Set<string>();
      return merged.filter((p) => (seen.has(p.relativePath) ? false : (seen.add(p.relativePath), true)));
    });
    if (!title) {
      const first = next[0]?.relativePath.split("/")[0];
      if (first) setTitle(first);
    }
  };

  const setRole = (idx: number, role: FileRole) =>
    setPicked((prev) =>
      prev.map((p, i) => {
        if (i === idx) return { ...p, role };
        // single mode: exactly one task (demote any other). multi_sheet: keep all.
        if (mode === "single" && role === "task" && p.role === "task") return { ...p, role: "info" };
        return p;
      }),
    );

  const setImagePolicy = (idx: number, imagePolicy: ImagePolicy) =>
    setPicked((prev) => prev.map((p, i) => (i === idx ? { ...p, imagePolicy } : p)));

  const setTier = (idx: number, tier: SourceTier) =>
    setPicked((prev) => prev.map((p, i) => (i === idx ? { ...p, tier } : p)));

  const removeAt = (idx: number) => setPicked((prev) => prev.filter((_, i) => i !== idx));

  const taskOk =
    mode === "material" ? true : mode === "multi_sheet" ? taskCount >= 1 : taskCount === 1;
  const canSubmit = title.trim() && picked.length > 0 && taskOk && !busy;

  const submit = async (action: "run" | "mark") => {
    setError(null);
    setBusy(true);
    try {
      setProgress(t("Creating project…"));
      const project = await createProject(title.trim(), undefined, mode, brief);
      for (let i = 0; i < picked.length; i++) {
        const p = picked[i];
        setProgress(t("Uploading {{current}}/{{total}}: {{name}}", { current: i + 1, total: picked.length, name: p.file.name }));
        await uploadProjectFile(
          project.id, p.file, p.relativePath, p.role, p.imagePolicy,
          p.tier === "support" ? p.tier : undefined,
        );
      }
      if (action === "mark") {
        navigate(`/app/projects/${project.id}/mark`);
        return;
      }
      // Text extraction runs async on the server (task-file Vision OCR). The
      // project flips draft→ready when it's done; starting the run before that
      // 409s. Wait for ready, then run. If it doesn't flip in time, the project
      // is saved — the detail page shows a "Generate summary" button.
      setProgress(t("Reading your files…"));
      const ready = await waitForProjectReady(project.id, 120_000);
      if (ready) {
        setProgress(t("Starting the agent…"));
        await runProject(project.id, { language, with_synthesis: withSynthesis });
      }
      navigate(`/app/projects/${project.id}`);
    } catch (e) {
      setError(generationErrorMessage(e, t, t("Something went wrong.")));
      setBusy(false);
      setProgress("");
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ink">{t("New project")}</h1>
        {mode === "material" ? (
          <p className="text-sm text-ink-soft">
            {t("Upload papers, notes or book chapters — no exercise sheet needed. The agent maps and teaches the material itself.")}
          </p>
        ) : (
          <p className="text-sm text-ink-soft">
            {mode === "multi_sheet"
              ? t("Upload your material and ALL the exercise sheets. Mark every sheet as a")
              : t("Upload the lecture material and the assignment. Mark exactly one file as the")}{" "}
            <strong>{t("task")}</strong>.
          </p>
        )}
      </div>

      {error && <Alert>{error}</Alert>}

      <div className="card flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="text-sm font-medium text-ink">{t("Project type")}</span>
          <p className="text-xs text-ink-faint">
            {mode === "multi_sheet"
              ? t("Coursework: many task sheets, each solved individually, with one theory intro + study sets across everything.")
              : mode === "material"
                ? t("Understand material: papers, notes or chapters — a breadth summary + study sets, no task to solve.")
                : t("Single assignment: one task, solved and turned into a study set.")}
          </p>
        </div>
        <div className="flex shrink-0 overflow-hidden rounded-lg border border-black/10 text-xs">
          {([
            { m: "single" as ProjectMode, label: t("Single assignment") },
            { m: "multi_sheet" as ProjectMode, label: t("Coursework (many sheets)") },
            { m: "material" as ProjectMode, label: t("Understand material") },
          ]).map(({ m, label }) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={
                "px-3 py-1.5 " +
                (mode === m ? "bg-brand-600 text-white" : "bg-white text-ink-soft hover:bg-black/5")
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
        <label className="block">
          <span className="text-sm font-medium text-ink">{t("Project title")}</span>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("e.g. Computational Macro — Assignment 2")} />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-ink">{t("Language")}</span>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="mt-1 h-11 w-full rounded-xl border border-black/10 bg-white px-3 text-sm"
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-ink">{t("What do you want? (optional)")}</span>
        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={2}
          maxLength={4000}
          placeholder={t("Explain in detail — the angle, emphasis, what to skip, what the output is for. This steers the summary, figures and questions.")}
          className="mt-1 w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm"
        />
      </label>

      <label className="card flex cursor-pointer items-center justify-between gap-3">
        <div>
          <span className="text-sm font-medium text-ink">{t("Add diverse practice (synthesis)")}</span>
          <p className="text-xs text-ink-faint">
            {t("Also generate extra questions that probe each topic from new angles — different assumptions, perspectives, and transfer cases.")}
          </p>
        </div>
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0"
          checked={withSynthesis}
          onChange={(e) => setWithSynthesis(e.target.checked)}
        />
      </label>

      <div className="card space-y-3">
        <div className="flex flex-wrap gap-2">
          <input
            ref={folderInput}
            type="file"
            webkitdirectory=""
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
          <input
            ref={filesInput}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
          <Button variant="secondary" onClick={() => folderInput.current?.click()}>
            {t("Upload a folder")}
          </Button>
          <Button variant="secondary" onClick={() => filesInput.current?.click()}>
            {t("Add files")}
          </Button>
        </div>

        {picked.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-faint">{t("No files selected yet.")}</p>
        ) : (
          <ul className="divide-y divide-black/5">
            {picked.map((p, idx) => (
              <li key={p.relativePath} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                <span className="min-w-[8rem] flex-1 truncate text-sm text-ink" title={p.relativePath}>
                  {p.relativePath}
                </span>
                <div className="flex shrink-0 overflow-hidden rounded-lg border border-black/10 text-xs">
                  {((mode === "material" ? ["info", "ignore"] : ["info", "task", "ignore"]) as FileRole[]).map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRole(idx, r)}
                      className={
                        "px-2.5 py-1 capitalize " +
                        (p.role === r ? "bg-brand-600 text-white" : "bg-white text-ink-soft hover:bg-black/5")
                      }
                    >
                      {r === "info" ? t("material") : t(r)}
                    </button>
                  ))}
                </div>
                {p.role === "info" && (
                  <div
                    className="flex shrink-0 overflow-hidden rounded-lg border border-black/10 text-xs"
                    title={t("Main sources are centered on; support files are background/reference only")}
                  >
                    {(["main", "support"] as SourceTier[]).map((tr) => (
                      <button
                        key={tr}
                        type="button"
                        onClick={() => setTier(idx, tr)}
                        className={
                          "px-2.5 py-1 capitalize " +
                          (p.tier === tr ? "bg-brand-600 text-white" : "bg-white text-ink-soft hover:bg-black/5")
                        }
                      >
                        {t(tr)}
                      </button>
                    ))}
                  </div>
                )}
                {p.role !== "ignore" && (
                  <div
                    className="flex shrink-0 overflow-hidden rounded-lg border border-black/10 text-xs"
                    title={t("Which images to pull from this file")}
                  >
                    <span className="bg-zinc-50 px-2 py-1 text-ink-faint">{t("Images")}</span>
                    {(["all", "none", "marked"] as ImagePolicy[]).map((ip) => (
                      <button
                        key={ip}
                        type="button"
                        onClick={() => setImagePolicy(idx, ip)}
                        className={
                          "px-2.5 py-1 capitalize " +
                          (p.imagePolicy === ip ? "bg-brand-600 text-white" : "bg-white text-ink-soft hover:bg-black/5")
                        }
                      >
                        {t(ip)}
                      </button>
                    ))}
                  </div>
                )}
                <button type="button" onClick={() => removeAt(idx)} className="text-ink-faint hover:text-rose-600">
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}

        {picked.length > 0 && !taskOk && (
          <p className="text-xs text-amber-700">
            {mode === "multi_sheet"
              ? t("Mark at least one file as a task sheet (currently {{count}}).", { count: taskCount })
              : t("Mark exactly one file as the task (currently {{count}}).", { count: taskCount })}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => submit("run")} disabled={!canSubmit}>
          {busy ? t("Working…") : t("Create & run agent")}
        </Button>
        <Button variant="secondary" onClick={() => submit("mark")} disabled={!canSubmit}>
          {t("Upload & mark first")}
        </Button>
        {busy && (
          <span className="inline-flex items-center gap-2 text-sm text-ink-soft">
            <Spinner className="h-4 w-4" /> {progress}
          </span>
        )}
      </div>
      <p className="text-xs text-ink-faint">
        {t("“Mark first” lets you open each file, highlight what matters, and save your progress before generating — the agent then focuses on exactly what you marked.")}
      </p>
    </div>
  );
}
